import { Layout } from '../components/Layout';

export function About() {
  return (
    <Layout theme="white">
      <div className="content-centered" style={{ textAlign: 'left', maxWidth: 700, padding: '40px 20px' }}>
        <h1 className="heading-lg" style={{ color: 'var(--text-primary)', marginBottom: 32, textAlign: 'center' }}>
          About Fairdrop
        </h1>

        <p style={{ fontSize: 20, fontStyle: 'italic', marginBottom: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
          "Share files. Not data."
        </p>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--text-primary)' }}>The Problem</h2>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Every time you share a file through WeTransfer, Dropbox, or Google Drive, you're feeding the surveillance machine.
            Your IP logged. Your files scanned. Your recipients tracked. Your patterns profiled.
          </p>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Even with encrypted files, most services still collect metadata: who you shared with, when, how often.
            For journalists, activists, and whistleblowers, this metadata is dangerous evidence.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--text-primary)' }}>Our Solution</h2>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <strong>Fairdrop collects nothing at all.</strong> Not encrypted-but-logged. Not private-but-metadata-tracked.
            Actually nothing. Zero data. Zero metadata. Zero surveillance.
          </p>
          <ul style={{ marginLeft: 20, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <li>Files encrypted on your device before upload</li>
            <li>Stored on Swarm, a decentralized network</li>
            <li>No IP logging, no email required, no tracking</li>
            <li>We can't comply with data requests—we have no data</li>
          </ul>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--text-primary)' }}>Honest Inbox</h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Receive files anonymously. Share your inbox link publicly, and anyone can send you files
            without revealing their identity. Perfect for whistleblowing, anonymous feedback, and secure tips.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--text-primary)' }}>Open Source</h2>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Everything is open source. The code is auditable. The cryptography is standard
            (secp256k1 ECDH + AES-256-GCM). We don't ask you to trust us—we ask you to verify.
          </p>
        </section>

        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--text-primary)' }}>Cypherpunk Heritage</h2>
          <p style={{ marginBottom: 16, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <em>"Cypherpunks write code. We know that someone has to write software to defend privacy,
            and we're going to write it."</em> — Eric Hughes, 1993
          </p>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Fairdrop follows in the tradition of PGP, Tor, Signal, and Bitcoin—tools built by people
            who saw gaps in privacy infrastructure and wrote the code to fill them.
          </p>
        </section>

        <section id="agents" style={{ marginBottom: 32, padding: 24, background: 'var(--dark)', borderRadius: 12 }}>
          <h2 style={{ fontSize: 22, marginBottom: 16, color: 'var(--white)' }}>For AI Agents</h2>
          <p style={{ marginBottom: 16, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            Fairdrop provides an MCP (Model Context Protocol) server that enables AI agents to send and receive
            encrypted files programmatically. Perfect for agent-to-agent communication, automated workflows, and
            privacy-preserving file transfers.
          </p>
          <div style={{ background: 'rgba(255,255,255,0.1)', padding: 16, borderRadius: 8, marginBottom: 16 }}>
            <p style={{ color: 'var(--coral)', fontFamily: 'monospace', fontSize: 14, marginBottom: 8 }}>
              MCP Endpoint
            </p>
            <code style={{ color: 'var(--white)', fontSize: 16 }}>
              https://mcp.fairdrop.xyz/
            </code>
          </div>
          <p style={{ marginBottom: 16, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            <strong>14 tools available:</strong> Upload, download, send encrypted, send anonymous, manage accounts,
            check inbox, create honest inboxes, and more.
          </p>
          <p style={{ marginBottom: 16, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            <strong>DataEscrow:</strong> Agents can buy and sell encrypted data using trustless on-chain escrows.
            The seller encrypts data, locks the decryption key on-chain, and gets paid only after the buyer verifies
            the content hash. No intermediaries, no trust required.{' '}
            <a
              href="https://github.com/fairDataSociety/fairdrop/blob/main/docs/DATA_ESCROW_EXPLAINED.md"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--coral)', textDecoration: 'underline' }}
            >
              Learn how it works →
            </a>
          </p>
          <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, fontSize: 14 }}>
            Install via npm:{' '}
            <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 8px', borderRadius: 4 }}>
              npx @fairdrop/mcp
            </code>
            {' '}or use the HTTP endpoint directly.
          </p>
          <p style={{ marginTop: 16 }}>
            <a
              href="https://github.com/fairDataSociety/fairdrop/tree/main/packages/mcp"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--coral)', textDecoration: 'underline' }}
            >
              View MCP documentation →
            </a>
          </p>
        </section>

        <div style={{ textAlign: 'center', marginTop: 48, paddingTop: 32, borderTop: '1px solid var(--border-light)' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            A project of <a href="https://fairdatasociety.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--coral)' }}>Fair Data Society</a>
          </p>
        </div>
      </div>
    </Layout>
  );
}
